import React, { memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    EmojiPicker,
    Icon,
    Menus,
    SegmentedButtons,
    Text,
    Tooltip,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { CodeEditor, useHorizontalResize } from '@Sage/ssm-web5';
import HtmlPreviewFrame from 'containers/components/HtmlPreviewFrame';
import VariablesSelector from 'containers/components/VariablesSelector';
import TitleInput from '../../TitleInput';
import { getLiteral } from 'utils/getLiteral';

import { formatCampaignReplaceInsert } from '../../formatCampaignReplaceInsert';
import HtmlImportDropzone from './HtmlImportDropzone';

import './styles.scss';

const HTML_EDITOR_OPTIONS = {
    scrollbar: {
        horizontal: 'hidden',
        vertical: 'auto',
    },
};

/** Campaign field ids like `-account.name-`. */
const VARIABLE_TEMPLATE_PATTERN = /-[\w.]+-/g;

const VIEW_MODES = [
    { key: 'split', label: 'label_marketing_campaigns_split_screen', icon: 'splitView' },
    { key: 'code', label: 'label_marketing_campaigns_code_only', icon: 'code' },
    { key: 'desktop', label: 'label_marketing_campaigns_desktop_preview', icon: 'tv' },
    { key: 'mobile', label: 'label_marketing_campaigns_mobile_preview', icon: 'mobile' },
];

const createInitialState = ({ initialHtml = '', initialTitle = '' } = {}) => ({
    draftTitle: initialTitle,
    draftHtml: initialHtml,
    activeViewMode: 'split',
    splitPaneWidth: 50,
    isSaving: false,
    isDraggingFile: false,
    importError: '',
    saveError: '',
    canUndo: false,
    canRedo: false,
});

const htmlEditorReducer = (state, action) => {
    switch (action.type) {
        case 'RESET_STATE':
            return createInitialState(action.payload);

        case 'SET_VIEW_MODE':
            if (state.activeViewMode === action.payload) {
                return state;
            }
            return {
                ...state,
                activeViewMode: action.payload,
            };

        case 'SET_SPLIT_PANE_WIDTH':
            if (state.splitPaneWidth === action.payload) {
                return state;
            }
            return {
                ...state,
                splitPaneWidth: action.payload,
            };

        case 'COMMIT_HTML_CHANGE':
            return {
                ...state,
                draftHtml: action.payload || '',
                importError: '',
                saveError: '',
            };

        case 'SET_IMPORT_ERROR':
            return {
                ...state,
                importError: action.payload,
            };

        case 'SET_SAVE_ERROR':
            return {
                ...state,
                saveError: action.payload,
            };

        case 'SET_IS_DRAGGING_FILE':
            if (state.isDraggingFile === action.payload) {
                return state;
            }
            return {
                ...state,
                isDraggingFile: action.payload,
            };

        case 'SET_HISTORY_AVAILABILITY':
            if (
                state.canUndo === action.payload.canUndo &&
                state.canRedo === action.payload.canRedo
            ) {
                return state;
            }
            return {
                ...state,
                canUndo: action.payload.canUndo,
                canRedo: action.payload.canRedo,
            };

        case 'START_SAVE':
            return {
                ...state,
                isSaving: true,
                saveError: '',
            };

        case 'FINISH_SAVE':
            return {
                ...state,
                isSaving: false,
            };

        case 'SET_DRAFT_TITLE':
            return {
                ...state,
                draftTitle: action.payload,
            };

        default:
            return state;
    }
};

const readHtmlFile = async (file) => {
    if (!file) return '';
    if (typeof file.text === 'function') {
        return file.text();
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result || '');
        reader.onerror = () => reject(new Error('Unable to read HTML file.'));
        reader.readAsText(file);
    });
};

const HtmlEditorOverlay = memo(
    ({
        initialHtml = '',
        initialTitle = '',
        isOpen = false,
        editableTitle = false,
        onPreview,
        onSave,
        onExitWithoutSaving,
    }) => {
        const theme = useTheme();
        const previewInputRef = useRef(null);
        const splitContainerRef = useRef(null);
        const codePaneRef = useRef(null);
        const editorInstanceRef = useRef(null);
        const monacoInstanceRef = useRef(null);
        const variableDecorationIdsRef = useRef([]);
        const editorDisposablesRef = useRef([]);
        const lastSelectionRef = useRef(null);
        const dragCounterRef = useRef(0);
        const wasOpenRef = useRef(false);
        const [editorSessionKey, setEditorSessionKey] = useState(0);
        const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);

        const [state, dispatch] = useReducer(
            htmlEditorReducer,
            {
                initialHtml,
                initialTitle,
            },
            createInitialState,
        );

        const {
            activeViewMode,
            canRedo,
            canUndo,
            draftHtml,
            draftTitle,
            importError,
            isDraggingFile,
            isSaving,
            saveError,
            splitPaneWidth,
        } = state;

        const syncHistoryAvailability = useCallback(() => {
            const model = editorInstanceRef.current?.getModel?.();
            const nextCanUndo = model?.canUndo?.();
            const nextCanRedo = model?.canRedo?.();

            dispatch({
                type: 'SET_HISTORY_AVAILABILITY',
                payload: {
                    canUndo:
                        typeof nextCanUndo === 'boolean'
                            ? nextCanUndo
                            : Boolean(draftHtml !== initialHtml),
                    canRedo: typeof nextCanRedo === 'boolean' ? nextCanRedo : false,
                },
            });
        }, [draftHtml, initialHtml]);

        useEffect(() => {
            const isOpening = isOpen && !wasOpenRef.current;
            wasOpenRef.current = isOpen;

            if (!isOpen) {
                return;
            }

            dispatch({
                type: 'RESET_STATE',
                payload: {
                    initialHtml,
                    initialTitle,
                },
            });

            if (isOpening) {
                setEditorSessionKey((currentKey) => currentKey + 1);
            }
        }, [initialHtml, initialTitle, isOpen]);

        useEffect(() => {
            syncHistoryAvailability();
        }, [draftHtml, syncHistoryAvailability]);

        const handleSplitResize = useCallback(({ percentageList }) => {
            dispatch({ type: 'SET_SPLIT_PANE_WIDTH', payload: percentageList });
        }, []);

        useHorizontalResize({
            targetRef: codePaneRef,
            containerRef: splitContainerRef,
            edgeWidth: 12,
            highlightColor: 'transparent',
            minWidth: 360,
            minPercentage: 30,
            maxPercentage: 70,
            onResize: handleSplitResize,
            enabled: isOpen && activeViewMode === 'split',
        });

        const hasUnsavedChanges = draftHtml !== initialHtml;
        const hasHtmlContent = !!draftHtml.trim();
        const isSplitView = activeViewMode === 'split';
        const isCodeOnlyView = activeViewMode === 'code';
        const isMobilePreview = activeViewMode === 'mobile';
        const isCodeVisible = isSplitView || isCodeOnlyView;
        const isPreviewVisible = !isCodeOnlyView;

        const commitHtmlChange = useCallback((nextHtml) => {
            dispatch({ type: 'COMMIT_HTML_CHANGE', payload: nextHtml });
        }, []);

        const replaceHtmlContent = useCallback(
            (nextHtml) => {
                const editor = editorInstanceRef.current;
                const model = editor?.getModel?.();
                const fullRange = model?.getFullModelRange?.();

                if (editor && model && fullRange) {
                    editor.pushUndoStop?.();
                    editor.executeEdits('html-editor-import', [
                        {
                            range: fullRange,
                            text: nextHtml || '',
                            forceMoveMarkers: true,
                        },
                    ]);
                    editor.pushUndoStop?.();
                    window.setTimeout(syncHistoryAvailability, 0);
                    return;
                }

                commitHtmlChange(nextHtml);
                window.setTimeout(syncHistoryAvailability, 0);
            },
            [commitHtmlChange, syncHistoryAvailability],
        );

        const disposeEditorListeners = useCallback(() => {
            editorDisposablesRef.current.forEach((disposable) => disposable?.dispose?.());
            editorDisposablesRef.current = [];
            variableDecorationIdsRef.current = [];
        }, []);

        const syncLastKnownSelection = useCallback((selection) => {
            if (selection) {
                lastSelectionRef.current = selection;
            }
        }, []);

        const focusEditor = useCallback(() => {
            editorInstanceRef.current?.focus?.();
        }, []);

        const getFallbackSelection = useCallback(() => {
            const editor = editorInstanceRef.current;
            const monacoInstance = monacoInstanceRef.current;
            const model = editor?.getModel?.();

            if (!model || !monacoInstance) {
                return null;
            }

            const lineNumber = model.getLineCount();
            const column = model.getLineMaxColumn(lineNumber);

            return new monacoInstance.Selection(lineNumber, column, lineNumber, column);
        }, []);

        const getInsertionSelection = useCallback(() => {
            const editor = editorInstanceRef.current;
            const liveSelection = editor?.getSelection?.();

            if (editor?.hasTextFocus?.() && liveSelection) {
                return liveSelection;
            }

            return lastSelectionRef.current || liveSelection || getFallbackSelection();
        }, [getFallbackSelection]);

        const createSelectionAfterInsert = useCallback((selection, insertedText) => {
            const monacoInstance = monacoInstanceRef.current;
            if (!selection || !monacoInstance) {
                return null;
            }

            const insertedLines = insertedText.split(/\r\n|\r|\n/);
            const endLineNumber = selection.startLineNumber + insertedLines.length - 1;
            const endColumn =
                insertedLines.length === 1
                    ? selection.startColumn + insertedLines[0].length
                    : insertedLines[insertedLines.length - 1].length + 1;

            return new monacoInstance.Selection(endLineNumber, endColumn, endLineNumber, endColumn);
        }, []);

        const insertTextAtCursor = useCallback(
            (textToInsert = '') => {
                const editor = editorInstanceRef.current;
                const selection = getInsertionSelection();

                if (!editor || !selection || !textToInsert) {
                    return;
                }

                editor.executeEdits('html-editor-toolbar', [
                    {
                        range: selection,
                        text: textToInsert,
                        forceMoveMarkers: true,
                    },
                ]);

                const nextSelection = createSelectionAfterInsert(selection, textToInsert);

                if (nextSelection) {
                    editor.setSelection(nextSelection);
                    editor.revealPositionInCenterIfOutsideViewport?.(nextSelection.getPosition());
                    syncLastKnownSelection(nextSelection);
                }

                focusEditor();
                window.setTimeout(syncHistoryAvailability, 0);
            },
            [
                createSelectionAfterInsert,
                focusEditor,
                getInsertionSelection,
                syncHistoryAvailability,
                syncLastKnownSelection,
            ],
        );

        const handleInsertVariable = useCallback(
            (variable) => {
                insertTextAtCursor(variable);
            },
            [insertTextAtCursor],
        );

        const handleInsertEmoji = useCallback(
            (_name, selectedEmoji) => {
                if (!selectedEmoji?.emoji) {
                    return;
                }

                insertTextAtCursor(selectedEmoji.emoji);
                setIsEmojiPickerVisible(false);
            },
            [insertTextAtCursor],
        );

        const handleEmojiTriggerClick = useCallback((event) => {
            event?.preventDefault?.();
            event?.stopPropagation?.();
            setIsEmojiPickerVisible((currentVisible) => !currentVisible);
        }, []);

        const syncVariableDecorations = useCallback(() => {
            const editor = editorInstanceRef.current;
            const monacoInstance = monacoInstanceRef.current;
            const model = editor?.getModel?.();

            if (!editor || !monacoInstance || !model) {
                return;
            }

            const decorations = Array.from(
                model.getValue().matchAll(VARIABLE_TEMPLATE_PATTERN),
            ).map((match) => {
                const startPosition = model.getPositionAt(match.index || 0);
                const endPosition = model.getPositionAt((match.index || 0) + match[0].length);

                return {
                    range: new monacoInstance.Range(
                        startPosition.lineNumber,
                        startPosition.column,
                        endPosition.lineNumber,
                        endPosition.column,
                    ),
                    options: {
                        inlineClassName: 'marketing-html-editor__template-variable-highlight',
                        stickiness:
                            monacoInstance.editor.TrackedRangeStickiness
                                .NeverGrowsWhenTypingAtEdges,
                    },
                };
            });

            variableDecorationIdsRef.current = editor.deltaDecorations(
                variableDecorationIdsRef.current,
                decorations,
            );
        }, []);

        const handleEditorMount = useCallback(
            (editor, monacoInstance) => {
                disposeEditorListeners();
                window.setTimeout(() => {
                    const model = editor.getModel?.();
                    const nextCanUndo = model?.canUndo?.();
                    const nextCanRedo = model?.canRedo?.();

                    dispatch({
                        type: 'SET_HISTORY_AVAILABILITY',
                        payload: {
                            canUndo: typeof nextCanUndo === 'boolean' ? nextCanUndo : false,
                            canRedo: typeof nextCanRedo === 'boolean' ? nextCanRedo : false,
                        },
                    });
                }, 0);

                editorInstanceRef.current = editor;
                monacoInstanceRef.current = monacoInstance;
                syncLastKnownSelection(editor.getSelection?.() || getFallbackSelection());
                syncVariableDecorations();

                editorDisposablesRef.current = [
                    editor.onDidChangeCursorSelection?.(({ selection }) => {
                        syncLastKnownSelection(selection);
                    }),
                    editor.onDidFocusEditorText?.(() => {
                        syncLastKnownSelection(editor.getSelection?.());
                    }),
                    editor.onDidBlurEditorText?.(() => {
                        syncLastKnownSelection(editor.getSelection?.());
                    }),
                    editor.onDidChangeModelContent?.(() => {
                        syncVariableDecorations();
                    }),
                ].filter(Boolean);
            },
            [
                disposeEditorListeners,
                getFallbackSelection,
                syncLastKnownSelection,
                syncVariableDecorations,
            ],
        );

        const handleUndo = useCallback(() => {
            editorInstanceRef.current?.trigger('html-editor', 'undo', null);
            window.setTimeout(syncHistoryAvailability, 0);
        }, [syncHistoryAvailability]);

        const handleRedo = useCallback(() => {
            editorInstanceRef.current?.trigger('html-editor', 'redo', null);
            window.setTimeout(syncHistoryAvailability, 0);
        }, [syncHistoryAvailability]);

        const importHtmlFile = useCallback(
            async (file) => {
                if (!file) return;

                const isHtmlFile =
                    file.type === 'text/html' || file.name?.toLowerCase().endsWith('.html');

                if (!isHtmlFile) {
                    dispatch({
                        type: 'SET_IMPORT_ERROR',
                        payload: 'Only HTML files are supported.',
                    });
                    return;
                }

                try {
                    const content = await readHtmlFile(file);
                    replaceHtmlContent(content);
                } catch (error) {
                    console.error(error);
                    dispatch({
                        type: 'SET_IMPORT_ERROR',
                        payload: 'Unable to import the selected HTML file.',
                    });
                }
            },
            [replaceHtmlContent],
        );

        const handleFileDrop = useCallback(
            async (files = []) => {
                const [file] = files;
                dragCounterRef.current = 0;
                dispatch({ type: 'SET_IS_DRAGGING_FILE', payload: false });
                await importHtmlFile(file);
            },
            [importHtmlFile],
        );

        const handleHiddenInputChange = useCallback(
            async (event) => {
                const [file] = Array.from(event.target.files || []);
                await importHtmlFile(file);
                event.target.value = '';
            },
            [importHtmlFile],
        );

        const openFileSelector = useCallback(() => {
            previewInputRef.current?.click?.();
        }, []);

        const handleDropzoneClick = useCallback(() => {
            openFileSelector();
        }, [openFileSelector]);

        const handleDropzoneKeyDown = useCallback(
            (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openFileSelector();
                }
            },
            [openFileSelector],
        );

        const handlePreviewDragEnter = useCallback((event) => {
            event.preventDefault();
            event.stopPropagation();
            dragCounterRef.current += 1;
            dispatch({ type: 'SET_IS_DRAGGING_FILE', payload: true });
        }, []);

        const handlePreviewDragOver = useCallback((event) => {
            event.preventDefault();
            event.stopPropagation();
        }, []);

        const handlePreviewDragLeave = useCallback((event) => {
            event.preventDefault();
            event.stopPropagation();
            dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
            if (dragCounterRef.current === 0) {
                dispatch({ type: 'SET_IS_DRAGGING_FILE', payload: false });
            }
        }, []);

        const handlePreviewDrop = useCallback(
            async (event) => {
                event.preventDefault();
                event.stopPropagation();
                const files = Array.from(event.dataTransfer?.files || []);
                await handleFileDrop(files);
            },
            [handleFileDrop],
        );

        const isFileDragEvent = useCallback((event) => {
            const types = Array.from(event.dataTransfer?.types || []);
            return (
                types.includes('Files') ||
                Array.from(event.dataTransfer?.items || []).some((item) => item?.kind === 'file')
            );
        }, []);

        const handleSave = useCallback(async () => {
            if (!onSave) return;

            dispatch({ type: 'START_SAVE' });

            try {
                await onSave({
                    html: draftHtml,
                    title: draftTitle,
                });
            } catch (error) {
                console.error(error);
                dispatch({
                    type: 'SET_SAVE_ERROR',
                    payload: 'Unable to save the current HTML draft.',
                });
            } finally {
                dispatch({ type: 'FINISH_SAVE' });
            }
        }, [draftHtml, draftTitle, onSave]);

        const handleSaveAndExit = useCallback(async () => {
            if (!onSave) return;

            dispatch({ type: 'START_SAVE' });

            try {
                await onSave({
                    html: draftHtml,
                    title: draftTitle,
                });
                onExitWithoutSaving?.(false);
            } catch (error) {
                console.error(error);
                dispatch({
                    type: 'SET_SAVE_ERROR',
                    payload: 'Unable to save the current HTML draft.',
                });
            } finally {
                dispatch({ type: 'FINISH_SAVE' });
            }
        }, [draftHtml, draftTitle, onExitWithoutSaving, onSave]);

        const handleViewModeChange = useCallback((nextViewMode) => {
            dispatch({ type: 'SET_VIEW_MODE', payload: nextViewMode });
        }, []);

        const handleTitleChange = useCallback((newTitle) => {
            dispatch({ type: 'SET_DRAFT_TITLE', payload: newTitle });
        }, []);

        const handleExitWithoutSaving = useCallback(() => {
            onExitWithoutSaving?.(true);
        }, [onExitWithoutSaving]);

        const handlePreviewAndTest = useCallback(() => {
            onPreview?.({
                title: draftTitle,
                html: draftHtml,
            });
        }, [draftHtml, draftTitle, onPreview]);

        useEffect(() => {
            if (!isOpen) return undefined;

            const handleKeyDown = (event) => {
                const isModifierPressed = event.ctrlKey || event.metaKey;
                const isUndoShortcut =
                    (isModifierPressed && !event.shiftKey && event.key.toLowerCase() === 'z') ||
                    (!isModifierPressed && event.altKey && event.key === 'Backspace');
                const isRedoShortcut =
                    (event.ctrlKey && event.key.toLowerCase() === 'y') ||
                    (isModifierPressed && event.shiftKey && event.key.toLowerCase() === 'z');

                if (isUndoShortcut) {
                    event.preventDefault();
                    handleUndo();
                }

                if (isRedoShortcut) {
                    event.preventDefault();
                    handleRedo();
                }
            };

            window.addEventListener('keydown', handleKeyDown, true);

            return () => {
                window.removeEventListener('keydown', handleKeyDown, true);
            };
        }, [handleRedo, handleUndo, isOpen]);

        useEffect(() => {
            if (!isOpen) return undefined;

            const handleWindowDragEnter = (event) => {
                if (!isFileDragEvent(event)) {
                    return;
                }

                event.preventDefault();
                dragCounterRef.current += 1;
                dispatch({ type: 'SET_IS_DRAGGING_FILE', payload: true });
            };

            const handleWindowDragOver = (event) => {
                if (!isFileDragEvent(event)) {
                    return;
                }

                event.preventDefault();
                dispatch({ type: 'SET_IS_DRAGGING_FILE', payload: true });
            };

            const handleWindowDragLeave = (event) => {
                if (!isFileDragEvent(event)) {
                    return;
                }

                event.preventDefault();
                dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);

                if (dragCounterRef.current === 0) {
                    dispatch({ type: 'SET_IS_DRAGGING_FILE', payload: false });
                }
            };

            const handleWindowDrop = async (event) => {
                if (!isFileDragEvent(event)) {
                    return;
                }

                event.preventDefault();
                event.stopPropagation();
                await handleFileDrop(Array.from(event.dataTransfer?.files || []));
            };

            window.addEventListener('dragenter', handleWindowDragEnter, true);
            window.addEventListener('dragover', handleWindowDragOver, true);
            window.addEventListener('dragleave', handleWindowDragLeave, true);
            window.addEventListener('drop', handleWindowDrop, true);

            return () => {
                window.removeEventListener('dragenter', handleWindowDragEnter, true);
                window.removeEventListener('dragover', handleWindowDragOver, true);
                window.removeEventListener('dragleave', handleWindowDragLeave, true);
                window.removeEventListener('drop', handleWindowDrop, true);
            };
        }, [handleFileDrop, isFileDragEvent, isOpen]);

        useEffect(() => {
            if (!isOpen) return undefined;

            const layoutTimer = window.setTimeout(() => {
                editorInstanceRef.current?.layout?.();
            }, 0);

            return () => {
                window.clearTimeout(layoutTimer);
            };
        }, [activeViewMode, isOpen]);

        useEffect(
            () => () => {
                disposeEditorListeners();
            },
            [disposeEditorListeners],
        );

        const menuItems = useMemo(
            () => [
                {
                    title: getLiteral('action_save'),
                    icon: <Icon name="save" />,
                    onClick: handleSave,
                },
                {
                    title: getLiteral('action_exit_without_saving'),
                    icon: <Icon name="logout" />,
                    onClick: handleExitWithoutSaving,
                },
            ],
            [handleExitWithoutSaving, handleSave],
        );

        const viewModeOptions = useMemo(
            () =>
                VIEW_MODES.map(({ key, label, icon }) => ({
                    name: key,
                    icon: (
                        <Tooltip content={getLiteral(label)}>
                            <span className="marketing-html-editor__segmented-icon">
                                <Icon
                                    name={icon}
                                    color={
                                        activeViewMode === key
                                            ? theme.colors.actionMajor[500]
                                            : theme.colors.actionMinor[500]
                                    }
                                />
                            </span>
                        </Tooltip>
                    ),
                })),
            [activeViewMode, theme.colors.actionMajor, theme.colors.actionMinor],
        );

        const previewContent = useMemo(() => {
            if (!hasHtmlContent) {
                return (
                    <HtmlImportDropzone
                        error={importError}
                        isDraggingFile={isDraggingFile}
                        onOpenFileSelector={handleDropzoneClick}
                        onDrop={handlePreviewDrop}
                        onDragEnter={handlePreviewDragEnter}
                        onDragOver={handlePreviewDragOver}
                        onDragLeave={handlePreviewDragLeave}
                        onKeyDown={handleDropzoneKeyDown}
                    />
                );
            }

            return (
                <>
                    <div
                        className={[
                            'marketing-html-editor__preview-surface',
                            isMobilePreview ? 'marketing-html-editor__preview-surface--mobile' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        onDragEnter={handlePreviewDragEnter}
                        onDragOver={handlePreviewDragOver}
                        onDragLeave={handlePreviewDragLeave}
                        onDrop={handlePreviewDrop}
                    >
                        <HtmlPreviewFrame
                            className="marketing-html-editor__iframe"
                            html={draftHtml}
                            title={
                                draftTitle ||
                                getLiteral('label_marketing_campaigns_design_html_editor')
                            }
                        />
                    </div>
                    {isDraggingFile && (
                        <HtmlImportDropzone
                            className="marketing-html-editor__empty-preview--overlay"
                            error={importError}
                            hideError
                            isDraggingFile={isDraggingFile}
                            onOpenFileSelector={handleDropzoneClick}
                            onDrop={handlePreviewDrop}
                            onDragEnter={handlePreviewDragEnter}
                            onDragOver={handlePreviewDragOver}
                            onDragLeave={handlePreviewDragLeave}
                            onKeyDown={handleDropzoneKeyDown}
                        />
                    )}
                </>
            );
        }, [
            draftHtml,
            draftTitle,
            handleDropzoneClick,
            handleDropzoneKeyDown,
            handlePreviewDragEnter,
            handlePreviewDragLeave,
            handlePreviewDragOver,
            handlePreviewDrop,
            hasHtmlContent,
            importError,
            isMobilePreview,
            isDraggingFile,
        ]);

        return (
            <div
                className={[
                    'marketing-html-editor',
                    isOpen ? 'marketing-html-editor--open' : 'marketing-html-editor--hidden',
                ].join(' ')}
            >
                <input
                    ref={previewInputRef}
                    type="file"
                    accept=".html,text/html"
                    className="marketing-html-editor__file-input"
                    onChange={handleHiddenInputChange}
                />
                <div className="marketing-html-editor__topbar">
                    <div className="marketing-html-editor__topbar-left">
                        {editableTitle ? (
                            <TitleInput
                                title={draftTitle}
                                onChange={handleTitleChange}
                                onSave={handleTitleChange}
                            />
                        ) : (
                            !!draftTitle?.trim() && (
                                <Text
                                    type="h6"
                                    className="marketing-html-editor__campaign-title"
                                    isTruncated
                                    useTooltip
                                >
                                    {draftTitle}
                                </Text>
                            )
                        )}
                        <div className="marketing-html-editor__history-actions">
                            <Icon
                                name="reply"
                                tooltip="Undo"
                                onClick={canUndo ? handleUndo : undefined}
                                size="large"
                                color={
                                    canUndo
                                        ? theme.colors.actionMajor[500]
                                        : theme.colors.actionMajor[150]
                                }
                            />
                            <Icon
                                name="resend"
                                tooltip="Redo"
                                onClick={canRedo ? handleRedo : undefined}
                                size="large"
                                color={
                                    canRedo
                                        ? theme.colors.actionMajor[500]
                                        : theme.colors.actionMajor[150]
                                }
                            />
                        </div>
                        <Text
                            type="caption"
                            className="marketing-html-editor__changes-status"
                            color={
                                hasUnsavedChanges
                                    ? theme.colors.actionMajor[500]
                                    : theme.colors.textLight.secondary
                            }
                        >
                            {hasUnsavedChanges
                                ? 'Unsaved changes'
                                : getLiteral('label_marketing_campaigns_no_unsaved_changes')}
                        </Text>
                    </div>
                    <div className="marketing-html-editor__topbar-right">
                        <SegmentedButtons
                            className="marketing-html-editor__view-modes"
                            value={activeViewMode}
                            onChange={handleViewModeChange}
                            options={viewModeOptions}
                        />
                        <Button
                            type="secondary"
                            onClick={handlePreviewAndTest}
                            isDisabled={!onPreview}
                        >
                            {getLiteral('label_marketing_campaigns_preview_and_test')}
                        </Button>
                        <Button onClick={handleSaveAndExit} isLoading={isSaving}>
                            {getLiteral('action_save_and_exit')}
                        </Button>
                        <Menus
                            size="small"
                            placement="bottom-end"
                            items={menuItems}
                            classNameMenu="marketing-html-editor__menu-popover"
                            icon={<Icon name="threeDots" />}
                        />
                    </div>
                </div>

                {(saveError || importError) && (
                    <div className="marketing-html-editor__messages">
                        {saveError && (
                            <Text
                                type="caption"
                                className="marketing-html-editor__error-copy"
                                color={theme.colors.semantic.negative500}
                            >
                                {saveError}
                            </Text>
                        )}
                        {importError && hasHtmlContent && (
                            <Text
                                type="caption"
                                className="marketing-html-editor__error-copy"
                                color={theme.colors.semantic.negative500}
                            >
                                {importError}
                            </Text>
                        )}
                    </div>
                )}

                <div className="marketing-html-editor__body">
                    <div
                        ref={splitContainerRef}
                        className={[
                            'marketing-html-editor__split-layout',
                            isSplitView ? 'marketing-html-editor__split-layout--split' : '',
                            isCodeOnlyView ? 'marketing-html-editor__split-layout--code' : '',
                            !isCodeVisible && isPreviewVisible
                                ? 'marketing-html-editor__split-layout--preview'
                                : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        {isCodeVisible && (
                            <div
                                ref={codePaneRef}
                                className={[
                                    'marketing-html-editor__code-pane',
                                    isSplitView
                                        ? 'marketing-html-editor__code-pane--resizable'
                                        : '',
                                    !isSplitView ? 'marketing-html-editor__code-pane--full' : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                style={isSplitView ? { width: `${splitPaneWidth}%` } : undefined}
                            >
                                <div className="marketing-html-editor__editor-toolbar">
                                    <div className="marketing-html-editor__insert-actions">
                                        <EmojiPicker
                                            popoverVisible={isEmojiPickerVisible}
                                            onVisibleChange={setIsEmojiPickerVisible}
                                            onSelectEmoji={handleInsertEmoji}
                                            onRequestFocus={focusEditor}
                                        >
                                            <span
                                                title={getLiteral('label_emoji')}
                                                onClick={handleEmojiTriggerClick}
                                            >
                                                <Icon
                                                    name="moodSmall"
                                                    color={theme.colors.actionMinor[500]}
                                                />
                                            </span>
                                        </EmojiPicker>
                                        <VariablesSelector
                                            onApply={handleInsertVariable}
                                            useCampaignReplaceVariables
                                            formatInsertedVariable={formatCampaignReplaceInsert}
                                        >
                                            <span title={getLiteral('title_insert_fields')}>
                                                <Icon
                                                    name="variables"
                                                    color={theme.colors.actionMinor[500]}
                                                />
                                            </span>
                                        </VariablesSelector>
                                    </div>
                                </div>
                                <CodeEditor
                                    key={editorSessionKey}
                                    className="marketing-html-editor__editor"
                                    language="html"
                                    options={HTML_EDITOR_OPTIONS}
                                    showFullscreenButton={false}
                                    value={draftHtml}
                                    onChange={commitHtmlChange}
                                    onMount={handleEditorMount}
                                />
                            </div>
                        )}
                        {isPreviewVisible && (
                            <div
                                className={[
                                    'marketing-html-editor__preview-pane',
                                    !isSplitView
                                        ? 'marketing-html-editor__preview-pane--single'
                                        : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                            >
                                {previewContent}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);

HtmlEditorOverlay.displayName = 'HtmlEditorOverlay';

HtmlEditorOverlay.propTypes = {
    campaignId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialHtml: PropTypes.string,
    initialTitle: PropTypes.string,
    isOpen: PropTypes.bool,
    editableTitle: PropTypes.bool,
    onPreview: PropTypes.func,
    onSave: PropTypes.func,
    onExitWithoutSaving: PropTypes.func,
};

export default HtmlEditorOverlay;
